/**
 * „Letzter Besuch je Objekt": wer war zuletzt wo, wer ist gerade vor Ort,
 * Ampel nach Soll-Intervall und Hinweis gegen Doppelfahrten.
 * Reine Funktionen – Server und Client (Offline, Outbox) rechnen gleich.
 *
 * Datenschutz: Ein Besuch enthält nur Vorname, Tag und ggf. Beginn-Uhrzeit –
 * keine Dauer, kein Ende, keine Notiz.
 */
import { addDays, berlinDate, berlinToUtc, daysBetween, entryInterval } from './time.js';

/** Konfiguration einer Kachel (ein Objekt oder eine Gruppe von Tätigkeiten, z. B. „Hub"). */
export interface VisitObject {
  key: string;
  label: string;
  /** Tätigkeiten, die als Besuch dieses Objekts zählen */
  activities: string[];
  /** Soll: mindestens ein Besuch alle N Tage (null = nur Info) */
  intervalDays: number | null;
  /** Hinweis „war erst … da", wenn der letzte Besuch höchstens so viele Tage zurückliegt (0 = aus) */
  recentDays: number;
}

export interface VisitRecord {
  objectKey: string;
  /** Zeiteintrag (UUID) – nötig, um lokale Änderungen aus der Outbox zu verrechnen */
  entryId: string;
  userId: number;
  userName: string;
  /** Besuchstag (lokal): Tag des Endes, bei laufendem Stempel heute */
  day: string;
  /** Beginn-Uhrzeit, wenn der Besuch am Besuchstag begann (sonst null, z. B. über Mitternacht) */
  time: string | null;
  /** Stempeluhr läuft noch */
  running: boolean;
}

export interface VisitEntryLike {
  id: string;
  userId: number;
  date: string;
  start: string;
  end: string | null;
  activity: string;
  deleted: boolean;
}

/** Laufende Stempelungen, die älter sind, gelten als vergessen und zählen nicht als „vor Ort". */
export const STALE_RUNNING_HOURS = 16;

export function objectKeyFor(objects: VisitObject[], activity: string): string | null {
  return objects.find((o) => o.activities.includes(activity))?.key ?? null;
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

/** Zeiteintrag → Besuch (null bei gelöschten, fremden Tätigkeiten oder vergessenen Stempeln). */
export function entryToVisit(e: VisitEntryLike, userName: string, objects: VisitObject[], now: number): VisitRecord | null {
  if (e.deleted) return null;
  const objectKey = objectKeyFor(objects, e.activity);
  if (!objectKey) return null;
  const base = { objectKey, entryId: e.id, userId: e.userId, userName: firstName(userName) };
  if (e.end === null) {
    const startMs = berlinToUtc(e.date, e.start);
    if (startMs > now || now - startMs > STALE_RUNNING_HOURS * 3_600_000) return null;
    const today = berlinDate(now);
    return { ...base, day: today, time: e.date === today ? e.start : null, running: true };
  }
  const iv = entryInterval(e.date, e.start, e.end);
  if (iv.startMs > now) return null;
  const day = berlinDate(iv.endMs);
  return { ...base, day, time: day === e.date ? e.start : null, running: false };
}

/** Sortierschlüssel: später = größer. Laufende Besuche zählen als „jetzt". */
function rank(r: VisitRecord): string {
  return `${r.day} ${r.running ? '99:99' : r.time ?? '00:00'}`;
}

export interface ObjectVisits {
  /** jüngste Besuche, neuester zuerst (höchstens `keep`) */
  recent: VisitRecord[];
  /** gerade vor Ort */
  present: VisitRecord[];
}

/** Besuche je Objekt zusammenfassen. */
export function summarizeVisits(records: VisitRecord[], keep = 3): Map<string, ObjectVisits> {
  const out = new Map<string, ObjectVisits>();
  const sorted = [...records].sort((a, b) => rank(b).localeCompare(rank(a)));
  for (const r of sorted) {
    const o = out.get(r.objectKey) ?? { recent: [], present: [] };
    if (r.running && !o.present.some((p) => p.userId === r.userId)) o.present.push(r);
    if (o.recent.length < keep) o.recent.push(r);
    out.set(r.objectKey, o);
  }
  return out;
}

/**
 * Lokale Änderungen (eigene Outbox) einrechnen: Einträge mit gleicher ID
 * ersetzen bzw. entfernen, neue hinzufügen.
 */
export function overlayVisits(server: VisitRecord[], local: { id: string; visit: VisitRecord | null }[]): VisitRecord[] {
  const touched = new Set(local.map((l) => l.id));
  return [...server.filter((r) => !touched.has(r.entryId)), ...local.flatMap((l) => (l.visit ? [l.visit] : []))];
}

export type Ampel = 'gruen' | 'gelb' | 'rot';

export interface VisitStatus {
  key: string;
  label: string;
  last: VisitRecord | null;
  present: VisitRecord[];
  /** Tage seit dem letzten Besuch (0 = heute) */
  daysSince: number | null;
  ampel: Ampel | null;
  /** Fällig am (letzter Besuchstag + Intervall) */
  dueDate: string | null;
  /** Tage bis fällig (negativ = überfällig) */
  daysUntilDue: number | null;
  /** Hinweis gegen Doppelfahrten */
  recentHint: string | null;
}

/**
 * Ampel: rot = überfällig (fällig-Tag überschritten oder noch nie besucht),
 * gelb = fällig in höchstens 1 Tag (auch „heute fällig"), grün = im Intervall.
 * Wer gerade vor Ort ist, zählt als Besuch heute.
 */
export function visitStatus(obj: VisitObject, v: ObjectVisits | undefined, now: number): VisitStatus {
  const today = berlinDate(now);
  const last = v?.recent[0] ?? null;
  const present = v?.present ?? [];
  const daysSince = last ? Math.max(0, daysBetween(last.day, today)) : null;
  let ampel: Ampel | null = null;
  let dueDate: string | null = null;
  let daysUntilDue: number | null = null;
  if (obj.intervalDays !== null) {
    if (!last) ampel = 'rot';
    else {
      dueDate = addDays(last.day, obj.intervalDays);
      daysUntilDue = daysBetween(today, dueDate);
      ampel = daysUntilDue < 0 ? 'rot' : daysUntilDue <= 1 ? 'gelb' : 'gruen';
    }
  }
  let recentHint: string | null = null;
  if (last && !present.length && obj.recentDays > 0 && daysSince !== null && daysSince <= obj.recentDays) {
    recentHint = daysSince === 0 ? 'war heute schon da' : daysSince === 1 ? 'war erst gestern da' : daysSince === 2 ? 'war erst vorgestern da' : `war erst vor ${daysSince} Tagen da`;
  }
  return { key: obj.key, label: obj.label, last, present, daysSince, ampel, dueDate, daysUntilDue, recentHint };
}

/** „heute", „gestern", „vor 3 Tagen" */
export function relativeDays(n: number): string {
  return n === 0 ? 'heute' : n === 1 ? 'gestern' : n === 2 ? 'vorgestern' : `vor ${n} Tagen`;
}

/** Aus mehreren Tätigkeits-Einstellungen die Kacheln bilden (Gruppe = gleiche Kachel). */
export interface ActivityVisitConfig {
  name: string;
  sort: number;
  active: boolean;
  showOnHome: boolean;
  visitGroup: string | null;
  intervalDays: number | null;
  recentDays: number;
}

export function buildVisitObjects(acts: ActivityVisitConfig[]): VisitObject[] {
  const byKey = new Map<string, VisitObject & { show: boolean; sort: number }>();
  for (const a of [...acts].sort((x, y) => x.sort - y.sort)) {
    const key = a.visitGroup?.trim() || a.name;
    const o = byKey.get(key) ?? { key, label: key, activities: [], intervalDays: null, recentDays: 0, show: false, sort: a.sort };
    o.activities.push(a.name);
    // Gruppe: das strengste Intervall und der längste Hinweis-Zeitraum gelten
    if (a.intervalDays !== null) o.intervalDays = o.intervalDays === null ? a.intervalDays : Math.min(o.intervalDays, a.intervalDays);
    o.recentDays = Math.max(o.recentDays, a.recentDays);
    o.show = o.show || (a.showOnHome && a.active);
    byKey.set(key, o);
  }
  return [...byKey.values()].filter((o) => o.show).map(({ show: _s, sort: _o, ...o }) => o);
}
